/**
 * mock.ts
 *
 * In-memory mock backend used when VITE_MOCK_API=1, so the taskpane UI
 * runs standalone in a plain browser with no FastAPI backend running.
 * State lives in module-scope variables and resets on page reload — that
 * is intentional for a dev fixture, not a bug.
 */
import {
  ApiError,
  type AdminSettings,
  type DevLoginResponse,
  type FilingFileResult,
  type FilingGroupResult,
  type FilingPrecheckRequest,
  type FilingPrecheckResponse,
  type FilingRequest,
  type FilingTargetResult,
  type IndexStatus,
  type ItemType,
  type MeResponse,
  type ProjectSearchResponse,
  type RecentFilingsResponse,
  type RecentFilingTarget,
  type SearchFilters,
  type Subscription,
  type SubscriptionListResponse,
  type UndoFilingTargetResponse,
  type UpdateAdminSettingsRequest,
} from "./types";

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ---------------------------------------------------------------------------
// Fake project catalog (~40 rows across divisions)
// ---------------------------------------------------------------------------

interface MockProjectSeed {
  code: string;
  name: string;
  division: string;
  year: string;
}

interface MockProject {
  id: number;
  project_code: string | null;
  name: string;
  division: string;
  year: string | null;
  path: string;
  parse_ok: boolean;
}

// Real division buckets are two-digit numbers (plus "Legacy", used below for
// the unparseable fixture) — mirrors the actual Z:\BE\Projects layout.
const DIVISIONS = ["10", "24", "32", "33", "40", "50", "61", "65", "72", "90"] as const;

const YEARS = ["24", "25", "26"] as const;

const CITIES = [
  "Riverside",
  "Cedar Falls",
  "Mount Pleasant",
  "Silver Lake",
  "Fairview",
  "Twin Oaks",
  "Harbor Point",
  "Stonebridge",
  "Maple Grove",
  "Sunset Ridge",
  "Elm Crossing",
  "Pine Hollow",
  "Willow Creek",
  "Copper Basin",
  "Redstone",
  "Clearwater",
  "Ashford",
  "Brookhaven",
  "Kingston",
  "Lakeview",
] as const;

const BUILDING_TYPES = [
  "Elementary School",
  "Middle School Addition",
  "Regional Hospital Wing",
  "Water Treatment Plant",
  "Corporate HQ Renovation",
  "Data Center",
  "Terminal Expansion",
  "Justice Center",
  "Rec Center",
  "Parking Structure",
  "Distribution Warehouse",
  "Public Library",
  "Fire Station",
  "Wastewater Facility",
  "Transit Hub",
] as const;

function buildSeedProjects(): MockProjectSeed[] {
  // The one project name the plan calls out by name — always present so
  // any fixed dev/demo script can search for it deterministically.
  const seeds: MockProjectSeed[] = [
    {
      code: "2665-00018-00",
      name: "La Verkin 1, 3, 7 UT # 5187540",
      division: "65",
      year: "26",
    },
  ];

  let n = 0;
  for (const city of CITIES) {
    for (let rotation = 0; rotation < 2; rotation++) {
      const buildingType = BUILDING_TYPES[(n + rotation) % BUILDING_TYPES.length];
      const division = DIVISIONS[n % DIVISIONS.length];
      const year = YEARS[n % YEARS.length];
      if (buildingType === undefined || division === undefined || year === undefined) {
        continue;
      }
      n += 1;
      const code = `${2300 + (n % 8) * 15}-${String(10000 + n * 37).slice(-5)}-00`;
      seeds.push({ code, name: `${city} ${buildingType}`, division, year });
    }
  }
  return seeds;
}

function buildMockProjects(): MockProject[] {
  const projects: MockProject[] = buildSeedProjects().map((seed, index) => {
    const id = index + 1;
    const rawFolderName = `${seed.code} - ${seed.name}`;
    return {
      id,
      project_code: seed.code,
      name: seed.name,
      division: seed.division,
      year: seed.year,
      path: `/Shared/BE/Projects/${seed.division}/${seed.year}/${rawFolderName}`,
      parse_ok: true,
    };
  });

  // One deliberately-unparseable crawl fixture — echoes the plan's
  // "project_code nullable" / parse_ok schema note for a real folder that
  // doesn't match the "{ProjectID} - {ProjectName}" convention.
  projects.push({
    id: projects.length + 1,
    project_code: null,
    name: "OLD ARCHIVE - do not use (unfiled)",
    division: "Legacy",
    year: null,
    path: "/Shared/BE/Projects/Legacy/OLD ARCHIVE - do not use (unfiled)",
    parse_ok: false,
  });

  return projects;
}

const MOCK_PROJECTS: MockProject[] = buildMockProjects();
const PROJECTS_BY_ID = new Map<number, MockProject>(
  MOCK_PROJECTS.map((p) => [p.id, p]),
);

/** Deliberately refuses subscription — exercises the 403 NO_EGNYTE_ACCESS
 * path the same way a real Egnyte self-permission check would. */
const NO_ACCESS_PROJECT_ID = 5;

const subscribedIds = new Set<number>([1, 6, 12]);

function toSubscription(p: MockProject): Subscription {
  return {
    project_id: p.id,
    project_code: p.project_code,
    name: p.name,
    division: p.division,
    year: p.year,
    path: p.path,
    last_used_at: null,
    pin_order: null,
  };
}

function matchesQuery(project: MockProject, query: string): boolean {
  if (!query) {
    return true;
  }
  const q = query.toLowerCase();
  const qNoHyphen = q.replace(/-/g, "");
  const code = (project.project_code ?? "").toLowerCase();
  const codeNoHyphen = code.replace(/-/g, "");
  return (
    code.includes(q) ||
    codeNoHyphen.includes(qNoHyphen) ||
    project.name.toLowerCase().includes(q) ||
    project.division.toLowerCase().includes(q)
  );
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

async function devLogin(token: string): Promise<DevLoginResponse> {
  await sleep(200);
  if (!token.trim()) {
    throw new ApiError(400, "INVALID_TOKEN", "Dev token is required");
  }
  return { jwt: `mock-jwt.${token.trim()}` };
}

async function me(): Promise<MeResponse> {
  await sleep(100);
  return {
    username: "dev-user",
    email: "dev-user@example.com",
    needs_reauth: false,
  };
}

// ---------------------------------------------------------------------------
// Projects + subscriptions
// ---------------------------------------------------------------------------

function matchesFilters(p: MockProject, filters?: SearchFilters): boolean {
  if (!filters) {
    return true;
  }
  if (filters.divisions.length && !filters.divisions.includes(p.division)) {
    return false;
  }
  if (filters.years.length && !(p.year && filters.years.includes(p.year))) {
    return false;
  }
  if (filters.pathPrefix) {
    return p.path.startsWith(filters.pathPrefix.replace(/\/+$/, "") + "/");
  }
  return true;
}

async function searchProjects(
  query: string,
  filters?: SearchFilters,
): Promise<ProjectSearchResponse> {
  await sleep(200);
  const trimmed = query.trim();
  const matched = MOCK_PROJECTS.filter(
    (p) => matchesQuery(p, trimmed) && matchesFilters(p, filters),
  ).slice(0, 30);
  return {
    results: matched.map((p) => ({
      id: p.id,
      project_code: p.project_code,
      name: p.name,
      division: p.division,
      year: p.year,
      path: p.path,
      subscribed: subscribedIds.has(p.id),
      parse_ok: p.parse_ok,
    })),
  };
}

async function listSubscriptions(): Promise<SubscriptionListResponse> {
  await sleep(150);
  const subs = MOCK_PROJECTS.filter((p) => subscribedIds.has(p.id)).map(
    toSubscription,
  );
  return { subscriptions: subs };
}

async function subscribe(projectId: number): Promise<Subscription> {
  await sleep(300);
  if (projectId === NO_ACCESS_PROJECT_ID) {
    throw new ApiError(
      403,
      "NO_EGNYTE_ACCESS",
      "You do not have Egnyte access to this project folder",
    );
  }
  const project = PROJECTS_BY_ID.get(projectId);
  if (!project) {
    throw new ApiError(404, "NOT_FOUND", `Project ${projectId} not found`);
  }
  subscribedIds.add(projectId);
  return toSubscription(project);
}

async function unsubscribe(projectId: number): Promise<void> {
  await sleep(200);
  subscribedIds.delete(projectId);
}

// ---------------------------------------------------------------------------
// Filings
// ---------------------------------------------------------------------------

const DEST_MAP: Record<ItemType, string> = {
  email: "02_Proj Docs/09_Emails",
  rfi: "07_Const Admin/03_RFIs",
  submittal: "07_Const Admin/04_Submittals",
};

/** `${internetMessageId}::${projectId}` -> already-filed. Backs both
 * precheck's duplicate warning and the actual dedup-on-refile behavior. */
const filedKeys = new Set<string>();
const filedMeta = new Map<string, { filed_by: string; filed_at: string }>();

const filingResultsByRequestId = new Map<string, FilingGroupResult>();
const recentFilings: RecentFilingTarget[] = [];
let nextTargetId = 1;

function mockDateStamp(sentDateTime: string): string {
  const d = new Date(sentDateTime);
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}`;
}

function mockSanitizeForFilename(text: string): string {
  return text
    .replace(/[\\/":<>|*?+]/g, "-")
    .slice(0, 80)
    .trim();
}

/** Simplified stand-in for the backend's real naming.py — good enough to
 * demo the UI, not a substitute for the (property-tested) real sanitizer. */
function mockEmlFilename(req: FilingRequest): string {
  const stamp = mockDateStamp(req.email.sentDateTime);
  const subject = mockSanitizeForFilename(req.email.subject);
  if (req.item_type === "rfi") {
    return `RFI-${req.ref_number}_${stamp}_${subject}.eml`;
  }
  if (req.item_type === "submittal") {
    return `SUB-${req.ref_number}_${stamp}_${subject}.eml`;
  }
  return `${stamp}_${subject}.eml`;
}

function mockAttachmentFilename(
  emlFilename: string,
  index: number,
  originalName: string,
): string {
  const prefix = emlFilename.replace(/\.eml$/, "");
  const nn = String(index + 1).padStart(2, "0");
  return `${prefix}_att${nn}_${mockSanitizeForFilename(originalName)}`;
}

async function precheck(
  req: FilingPrecheckRequest,
): Promise<FilingPrecheckResponse> {
  await sleep(150);
  const results = req.project_ids.map((projectId) => {
    const key = `${req.internet_message_id}::${projectId}`;
    const meta = filedMeta.get(key);
    return {
      project_id: projectId,
      duplicate: filedKeys.has(key),
      filed_by: meta?.filed_by ?? null,
      filed_at: meta?.filed_at ?? null,
    };
  });
  return { results };
}

async function createFiling(req: FilingRequest): Promise<FilingGroupResult> {
  const existing = filingResultsByRequestId.get(req.client_request_id);
  if (existing) {
    // client_request_id is UNIQUE server-side; a retry replays the same result.
    return existing;
  }

  await sleep(800);

  const emlFilename = mockEmlFilename(req);
  const targets: FilingTargetResult[] = req.project_ids.map((projectId) => {
    const project = PROJECTS_BY_ID.get(projectId);
    const destPath = project
      ? `${project.path}/${DEST_MAP[req.item_type]}`
      : "unknown";
    const key = `${req.email.internetMessageId}::${projectId}`;
    const duplicate = filedKeys.has(key);

    if (projectId === NO_ACCESS_PROJECT_ID) {
      const files: FilingFileResult[] = [
        {
          kind: "eml",
          filename: emlFilename,
          status: "failed",
          error: "NO_EGNYTE_ACCESS: no permission to write to this folder",
        },
      ];
      return {
        project_id: projectId,
        dest_path: destPath,
        status: "failed",
        duplicate,
        files,
      };
    }

    filedKeys.add(key);
    filedMeta.set(key, {
      filed_by: "dev-user",
      filed_at: new Date().toISOString(),
    });

    const files: FilingFileResult[] = [
      { kind: "eml", filename: emlFilename, status: "uploaded" },
      ...req.email.attachments.map((att, index) => ({
        kind: "attachment" as const,
        filename: mockAttachmentFilename(emlFilename, index, att.name),
        status: "uploaded" as const,
      })),
    ];

    return {
      project_id: projectId,
      dest_path: destPath,
      status: "filed",
      duplicate,
      files,
    };
  });

  const result: FilingGroupResult = { group_id: crypto.randomUUID(), targets };
  filingResultsByRequestId.set(req.client_request_id, result);

  for (const target of targets) {
    const project = PROJECTS_BY_ID.get(target.project_id);
    recentFilings.unshift({
      target_id: nextTargetId++,
      filing_id: result.group_id,
      subject: req.email.subject,
      item_type: req.item_type,
      ref_number: req.ref_number ?? null,
      project_id: target.project_id,
      project_code: project?.project_code ?? null,
      project_name: project?.name ?? "Unknown project",
      dest_path: target.dest_path,
      status: target.status,
      filed_at: new Date().toISOString(),
      can_undo: target.status === "filed" || target.status === "partial",
    });
  }

  return result;
}

async function recentFilingsList(): Promise<RecentFilingsResponse> {
  await sleep(150);
  return { filings: recentFilings.slice(0, 50) };
}

async function undoTarget(targetId: number): Promise<UndoFilingTargetResponse> {
  await sleep(300);
  const target = recentFilings.find((f) => f.target_id === targetId);
  if (!target) {
    throw new ApiError(404, "NOT_FOUND", `Filing target ${targetId} not found`);
  }
  if (!target.can_undo) {
    throw new ApiError(
      400,
      "NOT_UNDOABLE",
      "This filing target can no longer be undone",
    );
  }
  target.status = "undone";
  target.can_undo = false;
  return { target_id: targetId, status: "undone" };
}

// ---------------------------------------------------------------------------
// Index status + admin settings
// ---------------------------------------------------------------------------

// Reports "demo" so the picker's demo banner is exercisable in mock mode —
// that is exactly the state the real deployment is in until a listing import
// or the Egnyte backend replaces the seeded tree.
async function indexStatus(): Promise<IndexStatus> {
  await sleep(80);
  return {
    data_source: "demo",
    projects_active: MOCK_PROJECTS.length,
    last_indexed_at: new Date().toISOString(),
    last_status: "success",
  };
}

let mockAdminSettings: AdminSettings = {
  egnyte_root: "/Shared/BE/Projects",
  sync_divisions: [...DIVISIONS],
  defaults: {
    egnyte_root: "/Shared/BE/Projects",
    sync_divisions: [...DIVISIONS],
  },
  overridden: [],
  storage_backend: "localdev",
};

async function adminSettings(): Promise<AdminSettings> {
  await sleep(120);
  return mockAdminSettings;
}

async function updateAdminSettings(
  body: UpdateAdminSettingsRequest,
): Promise<AdminSettings> {
  await sleep(200);
  if (body.egnyte_root !== undefined && !body.egnyte_root.startsWith("/")) {
    throw new ApiError(
      400,
      "INVALID_SETTING",
      "egnyte_root must be an absolute Egnyte path starting with '/'",
    );
  }
  const overridden = new Set(mockAdminSettings.overridden);
  const next = { ...mockAdminSettings };
  if (body.egnyte_root !== undefined) {
    next.egnyte_root = body.egnyte_root.replace(/\/+$/, "");
    overridden.add("egnyte_root");
  }
  if (body.sync_divisions !== undefined) {
    next.sync_divisions = body.sync_divisions;
    overridden.add("sync_divisions");
  }
  next.overridden = [...overridden].sort();
  mockAdminSettings = next;
  return mockAdminSettings;
}

async function resetAdminSetting(key: string): Promise<AdminSettings> {
  await sleep(150);
  const next = { ...mockAdminSettings };
  if (key === "egnyte_root") {
    next.egnyte_root = next.defaults.egnyte_root;
  } else if (key === "sync_divisions") {
    next.sync_divisions = [...next.defaults.sync_divisions];
  }
  next.overridden = mockAdminSettings.overridden.filter((k) => k !== key);
  mockAdminSettings = next;
  return mockAdminSettings;
}

export const mockApi = {
  devLogin,
  me,
  searchProjects,
  listSubscriptions,
  subscribe,
  unsubscribe,
  precheck,
  createFiling,
  recentFilings: recentFilingsList,
  undoTarget,
  indexStatus,
  adminSettings,
  updateAdminSettings,
  resetAdminSetting,
};
